import { Gender } from "../../common/interfaces/enums/Gender";
import { PaymentFrequency } from "../../common/interfaces/enums/PaymentFrequency";
import PaymentPeriod from "../../common/interfaces/enums/PaymentPeriod";
import { PremiumStructure } from "../../common/interfaces/enums/PremiumStructure";
import ProductType, {
    ClientProductTypes,
    ClientProductTypesList
} from "../../common/interfaces/enums/ProductType";
import { SmokerStatus } from "../../common/interfaces/enums/SmokerStatus";
import SortBy from "../enums/SortBy";
import TraumaType from "../../common/interfaces/enums/TraumaType";
import IBaseQuoteClient from "../interfaces/form/IBaseQuoteClient";
import { IQuoteClient } from "../interfaces/form/IQuoteClient";
import { IQuoteSettings } from "../interfaces/form/IQuoteSettings";
import { IOccupationQuoteClient } from "../interfaces/form/occupation/IOccupationQuoteClient";
import { IClient } from "../../common/interfaces/IClient";
import ISettingClient from "../../common/interfaces/settings/clients/ISettingClient";
import { IQuoteSettingContainerHealth } from "../../common/interfaces/settings/IQuoteSettingContainerHealth";
import { IQuoteSettingContainerIncome } from "../../common/interfaces/settings/IQuoteSettingContainerIncome";
import { IQuoteSettingContainerLife } from "../../common/interfaces/settings/IQuoteSettingContainerLife";
import { IQuoteSettingContainerMortgage } from "../../common/interfaces/settings/IQuoteSettingContainerMortgage";
import { IQuoteSettingContainerTrauma } from "../../common/interfaces/settings/IQuoteSettingContainerTrauma";
import { IQuoteSettingContainerWaiverOfPremium } from "../../common/interfaces/settings/IQuoteSettingsContainerWaiverOfPremium";
import { calculateMaxIncomeMonthlyCover } from "../../common/shared/productHelpers";
import { QuoteSettingContainerType } from "../../common/interfaces/settings/QuoteSettingContainerType";

interface ISettingsBuilder {
    build: (form: IQuoteSettings, productType: ProductType) => QuoteSettingContainerType;
    buildClient: (form: IQuoteSettings) => IClient[];
    buildQuoteSettings: (
        products: ProductType[],
        settings: QuoteSettingContainerType[],
        clients: IClient[]
    ) => IQuoteSettings;
    defaultAdult: IClient;
    defaultChild: IClient;
    defaultQuoteClient: { [key in ProductType]: IQuoteClient };
}

const baseSettings = {
    caseId: 0,
    addons: [],
    isLatestVersion: true,
    versionId: 0
};

const defaultAdult: IClient = {
    age: 35,
    gender: Gender.Unknown,
    isActive: true,
    isChild: false,
    personId: 0,
    smokerStatus: SmokerStatus.Unknown
};

const defaultChild: IClient = {
    age: 1,
    gender: Gender.Unknown,
    isActive: true,
    isChild: true,
    personId: 0,
    smokerStatus: SmokerStatus.Unknown
};

const defaultQuoteClient: {
    [key in ProductType]: IQuoteClient;
} = {
    [ProductType.Life]: {
        ...defaultAdult,
        [ProductType.Life]: {
            isSelected: true,
            coverAmount: 500000
        }
    },
    [ProductType.Trauma]: {
        ...defaultAdult,
        [ProductType.Trauma]: {
            isSelected: true,
            coverAmount: 100000,
            traumaType: TraumaType.Standalone
        }
    },
    [ProductType.Health]: {
        ...defaultAdult,
        [ProductType.Health]: {
            isSelected: true,
            excess: 500,
            hasDentalAndOpticalOption: false,
            hasGPOption: false,
            hasSpecialistsAndTestsOption: true
        }
    },
    [ProductType.Income]: {
        ...defaultAdult,
        [ProductType.Income]: {
            isSelected: true,
            monthlyCover: undefined,
            paymentPeriod: PaymentPeriod.UpToAgeSixtyFive,
            waitPeriodWeeks: 13
        }
    },
    [ProductType.Mortgage]: {
        ...defaultAdult,
        [ProductType.Mortgage]: {
            isSelected: true,
            monthlyCover: undefined,
            paymentPeriod: PaymentPeriod.UpToAgeSixtyFive,
            waitPeriodWeeks: 13
        }
    },
    [ProductType.WaiverOfPremium]: { ...defaultAdult }
};

const filterClients = (
    clients: IQuoteClient[],
    allowChildren: boolean,
    productTypes: ClientProductTypes[] // If undefined, then don't check products
): IQuoteClient[] => {
    return [...clients].filter((c) => {
        if (!allowChildren && c.isChild) return false;

        const hasProductSelected = productTypes.some((s) => c[s]?.isSelected);
        return hasProductSelected;
    });
};

const useSettingsBuilder = (): ISettingsBuilder => {
    const buildBaseClient = (
        c: IQuoteClient,
        setting: IBaseQuoteClient
    ): ISettingClient => {
        return {
            isSelected: setting.isSelected,
            personId: c.personId
        };
    };

    const isClientSelected = (
        client: ISettingClient,
        quoteClient: IQuoteClient,
        setting: QuoteSettingContainerType
    ): boolean => {
        if (!setting.allowChildren && quoteClient.isChild) return false;

        return typeof client?.isSelected === "boolean" ? client.isSelected : true;
    };

    const buildLife = (form: IQuoteSettings): IQuoteSettingContainerLife => {
        return {
            ...baseSettings,
            allowChildren: false,
            paymentFrequency: form.paymentFrequency,
            premiumStructure: form[ProductType.Life]?.premiumStructure,
            productType: ProductType.Life,
            sortBy: form.sortBy,
            clients: filterClients(form.clients, false, [ProductType.Life]).map((c) => {
                const setting = c[ProductType.Life];

                return {
                    ...buildBaseClient(c, setting),
                    coverAmount: setting?.coverAmount,
                    premiumStructure: form[ProductType.Life]?.premiumStructure
                };
            })
        };
    };

    const buildTrauma = (form: IQuoteSettings): IQuoteSettingContainerTrauma => {
        return {
            ...baseSettings,
            allowChildren: false,
            paymentFrequency: form.paymentFrequency,
            sortBy: form.sortBy,
            productType: ProductType.Trauma,
            clients: filterClients(form.clients, false, [ProductType.Trauma]).map((c) => {
                const setting = c[ProductType.Trauma];
                return {
                    ...buildBaseClient(c, setting),
                    coverAmount: setting?.coverAmount,
                    lifeCoverAmount: c[ProductType.Life]?.coverAmount,
                    traumaType: setting?.traumaType
                };
            })
        };
    };

    const buildHealth = (form: IQuoteSettings): IQuoteSettingContainerHealth => {
        return {
            ...baseSettings,
            allowChildren: true,
            paymentFrequency: form.paymentFrequency,
            sortBy: form.sortBy,
            productType: ProductType.Health,
            clients: filterClients(form.clients, true, [ProductType.Health]).map((c) => {
                const setting = c[ProductType.Health];

                return {
                    ...buildBaseClient(c, setting),
                    excess: setting?.excess,
                    hasDentalAndOpticalOption: setting?.hasDentalAndOpticalOption,
                    hasGPOption: setting?.hasGPOption,
                    hasSpecialistsAndTestsOption: setting?.hasSpecialistsAndTestsOption
                };
            })
        };
    };

    const buildIncome = (form: IQuoteSettings): IQuoteSettingContainerIncome => {
        return {
            ...baseSettings,
            allowChildren: false,
            paymentFrequency: form.paymentFrequency,
            sortBy: form.sortBy,
            productType: ProductType.Income,
            clients: filterClients(form.clients, false, [ProductType.Income]).map((c) => {
                const setting = c[ProductType.Income];

                const monthlyCover =
                    setting?.monthlyCover ||
                    (c.occupation?.income &&
                        calculateMaxIncomeMonthlyCover(c.occupation?.income)) ||
                    undefined;

                return {
                    ...buildBaseClient(c, setting),
                    employmentType: c.occupation?.employmentType,
                    income: c.occupation?.income,
                    monthlyCover: monthlyCover,
                    occupation: c.occupation?.name,
                    occupationClass: c.occupation?.class,
                    paymentPeriod: setting?.paymentPeriod,
                    waitPeriodWeeks: setting?.waitPeriodWeeks
                };
            })
        };
    };

    const buildMortgage = (form: IQuoteSettings): IQuoteSettingContainerMortgage => {
        return {
            ...baseSettings,
            allowChildren: false,
            paymentFrequency: form.paymentFrequency,
            sortBy: form.sortBy,
            productType: ProductType.Mortgage,
            mortgageAmount: form[ProductType.Mortgage]?.mortgageAmount,
            repaymentAmount: form[ProductType.Mortgage]?.repaymentAmount,
            repaymentFrequency: form[ProductType.Mortgage]?.repaymentFrequency,
            clients: filterClients(form.clients, false, [ProductType.Mortgage]).map(
                (c) => {
                    const setting = c[ProductType.Mortgage];

                    return {
                        ...buildBaseClient(c, setting),
                        employmentType: c.occupation?.employmentType,
                        income: c.occupation?.income,
                        monthlyCover: setting?.monthlyCover,
                        occupation: c.occupation?.name,
                        occupationClass: c.occupation?.class,
                        paymentPeriod: setting?.paymentPeriod,
                        waitPeriodWeeks: setting?.waitPeriodWeeks
                    };
                }
            )
        };
    };

    const buildWOP = (form: IQuoteSettings): IQuoteSettingContainerWaiverOfPremium => {
        return {
            ...baseSettings,
            allowChildren: false,
            paymentFrequency: form.paymentFrequency,
            sortBy: form.sortBy,
            productType: ProductType.WaiverOfPremium,
            clients: filterClients(form.clients, false, [
                ProductType.Mortgage,
                ProductType.Income
            ]).map((c) => {
                const setting = c[ProductType.WaiverOfPremium];

                return {
                    personId: c.personId,
                    isSelected:
                        c[ProductType.Mortgage].isSelected ||
                        c[ProductType.Income].isSelected,
                    employmentType: c.occupation?.employmentType,
                    income: c.occupation?.income,
                    monthlyCover: setting?.monthlyCover,
                    occupation: c.occupation?.name,
                    occupationClass: c.occupation?.class,
                    paymentPeriod: setting?.paymentPeriod,
                    waitPeriodWeeks: setting?.waitPeriodWeeks
                };
            })
        };
    };

    const build = (form, productType): QuoteSettingContainerType => {
        switch (productType) {
            case ProductType.Life:
                return buildLife(form);
            case ProductType.Trauma:
                return buildTrauma(form);
            case ProductType.Health:
                return buildHealth(form);
            case ProductType.Income:
                return buildIncome(form);
            case ProductType.Mortgage:
                return buildMortgage(form);
            case ProductType.WaiverOfPremium:
                return buildWOP(form);
            default:
                throw new Error(`Not supported product type - ${productType}`);
        }
    };

    const buildFormSettingsLife = (
        quoteSetting: IQuoteSettings,
        setting: IQuoteSettingContainerLife
    ): IQuoteSettings => {
        quoteSetting[ProductType.Life] = {
            premiumStructure: setting.premiumStructure || PremiumStructure.Stepped
        };

        quoteSetting.clients.forEach((s) => {
            const client = setting.clients.find((c) => c.personId === s.personId);
            s[ProductType.Life] = {
                isSelected: isClientSelected(client, s, setting),
                coverAmount: client?.coverAmount || 500000
            };
        });

        return quoteSetting;
    };

    const buildFormSettingsTrauma = (
        quoteSetting: IQuoteSettings,
        setting: IQuoteSettingContainerTrauma
    ): IQuoteSettings => {
        quoteSetting.clients.forEach((s) => {
            const client = setting.clients.find((c) => c.personId === s.personId);

            s[ProductType.Trauma] = {
                isSelected: isClientSelected(client, s, setting),
                coverAmount: client?.coverAmount || 100000,
                traumaType: client?.traumaType || TraumaType.Standalone
            };
        });

        return quoteSetting;
    };

    const buildFormSettingsHealth = (
        quoteSetting: IQuoteSettings,
        setting: IQuoteSettingContainerHealth
    ): IQuoteSettings => {
        quoteSetting.clients.forEach((s) => {
            const client = setting.clients.find((c) => c.personId === s.personId);

            let excess = client?.excess;
            if (isNaN(excess)) excess = 500;

            s[ProductType.Health] = {
                isSelected: isClientSelected(client, s, setting),
                excess,
                hasDentalAndOpticalOption: (client ? client.hasDentalAndOpticalOption : false),
                hasGPOption: (client ? client.hasGPOption : false),
                hasSpecialistsAndTestsOption: (client ? client.hasSpecialistsAndTestsOption : true)
            };
        });

        return quoteSetting;
    };

    const buildFormSettingsIncome = (
        quoteSetting: IQuoteSettings,
        setting: IQuoteSettingContainerIncome
    ): IQuoteSettings => {
        quoteSetting.clients.forEach((s) => {
            const client = setting.clients.find((c) => c.personId === s.personId);

            const occupation: IOccupationQuoteClient = {
                class: s.occupation?.class || client?.occupationClass,
                employmentType: s.occupation?.employmentType || client?.employmentType,
                income: s.occupation?.income || client?.income,
                name: s.occupation?.name || client?.occupation
            };

            const monthlyCover =
                client?.monthlyCover ||
                calculateMaxIncomeMonthlyCover(s.occupation?.income) ||
                undefined; // Undefined to have an empty, field rather than 0.

            s[ProductType.Income] = {
                isSelected: isClientSelected(client, s, setting),
                monthlyCover,
                paymentPeriod: client?.paymentPeriod || PaymentPeriod.UpToAgeSixtyFive,
                waitPeriodWeeks: client?.waitPeriodWeeks || 13
            };
            s.occupation = occupation;
        });
        return quoteSetting;
    };

    const buildFormSettingsMortgage = (
        quoteSetting: IQuoteSettings,
        setting: IQuoteSettingContainerMortgage
    ): IQuoteSettings => {
        quoteSetting[ProductType.Mortgage] = {
            mortgageAmount: setting.mortgageAmount,
            repaymentAmount: setting.repaymentAmount,
            repaymentFrequency: setting.repaymentFrequency
        };

        quoteSetting.clients.forEach((s) => {
            const client = setting.clients.find((c) => c.personId === s.personId);

            const occupation: IOccupationQuoteClient = {
                class: s.occupation?.class || client?.occupationClass,
                employmentType: s.occupation?.employmentType || client?.employmentType,
                income: s.occupation?.income || client?.income,
                name: s.occupation?.name || client?.occupation
            };

            s[ProductType.Mortgage] = {
                isSelected: isClientSelected(client, s, setting),
                monthlyCover: client?.monthlyCover,
                paymentPeriod: client?.paymentPeriod || PaymentPeriod.UpToAgeSixtyFive,
                waitPeriodWeeks: client?.waitPeriodWeeks || 13
            };
            s.occupation = occupation;
        });

        return quoteSetting;
    };

    const buildFormSettingsWaiverOfPremium = (
        quoteSetting: IQuoteSettings,
        setting: IQuoteSettingContainerWaiverOfPremium
    ): IQuoteSettings => {
        quoteSetting.clients = quoteSetting.clients.map((s) => {
            const wopClient = setting.clients.find((c) => c.personId === s.personId);

            return {
                ...s,
                [ProductType.WaiverOfPremium]: {
                    monthlyCover: wopClient.monthlyCover,
                    paymentPeriod: wopClient.paymentPeriod,
                    waitPeriodWeeks: wopClient.waitPeriodWeeks
                },
                occupation: {
                    class: wopClient.occupationClass,
                    employmentType: wopClient.employmentType,
                    income: wopClient.income,
                    name: wopClient.occupation || ""
                }
            };
        });

        return quoteSetting;
    };

    const buildQuoteSettings = (
        products: ProductType[],
        settings: QuoteSettingContainerType[],
        clients: IClient[]
    ): IQuoteSettings => {
        const clientList: IQuoteClient[] = clients.map(
            (baseClient): IQuoteClient => {
                return {
                    age: baseClient.age,
                    gender: baseClient.gender,
                    isActive: baseClient.isActive,
                    isChild: baseClient.isChild,
                    personId: baseClient.personId,
                    smokerStatus: baseClient.smokerStatus
                };
            }
        );

        let quoteSettings: IQuoteSettings = {
            paymentFrequency: PaymentFrequency.Fortnightly,
            sortBy: SortBy.LowestPrice,
            clients: clientList
        };

        // Check for duplicate product types

        const productList = new Set(settings.map((s) => s.productType));
        if (productList.size !== settings.length) {
            throw new Error("Duplicate products found, can't create quote settings");
        }

        for (const s of settings) {
            switch (s.productType) {
                case ProductType.Life:
                    quoteSettings = buildFormSettingsLife(quoteSettings, s);
                    break;
                case ProductType.Trauma:
                    quoteSettings = buildFormSettingsTrauma(quoteSettings, s);
                    break;
                case ProductType.Health:
                    quoteSettings = buildFormSettingsHealth(quoteSettings, s);
                    break;
                case ProductType.Income:
                    quoteSettings = buildFormSettingsIncome(quoteSettings, s);
                    break;
                case ProductType.Mortgage:
                    quoteSettings = buildFormSettingsMortgage(quoteSettings, s);
                    break;
                case ProductType.WaiverOfPremium:
                    quoteSettings = buildFormSettingsWaiverOfPremium(quoteSettings, s);
                    break;
                default:
                    throw new Error(`Unable to add settings to quote form`);
            }
        }

        const hasAnySelectedProduct = (client: IQuoteClient): boolean => {
            return products.some((p: ClientProductTypes) => client[p]?.isSelected);
        };

        // If we don't have any selected adults, select all of them
        if (quoteSettings.clients.every((client) => !hasAnySelectedProduct(client))) {
            quoteSettings.clients = quoteSettings.clients.map((c) => {
                if (c.isChild) return c;

                const baseAdultClient = {
                    ...c
                };

                const products = ClientProductTypesList.filter((s) =>
                    Object.keys(c).includes(s)
                );

                products.forEach((product) => {
                    baseAdultClient[product].isSelected = true;
                });

                return baseAdultClient;
            });
        }

        // // If we don't have a client, we need at least one
        if (quoteSettings.clients.length === 0) {
            quoteSettings.clients = [{ ...defaultAdult }];
        }

        // If we aren't doing any products, then all clients should be selected (For client settings)
        if (products.length === 0) {
            quoteSettings.clients = quoteSettings.clients.map((s) => {
                return {
                    ...s,
                    isSelected: true
                };
            });
        }

        return quoteSettings;
    };

    const buildClient = (form: IQuoteSettings): IClient[] => {
        const initialClients: IClient[] = form.clients.map((client) => {
            return {
                age: client.age,
                isActive: !!client.isActive,
                isChild: !!client.isChild,
                personId: client.personId || 0,
                gender: client.gender || Gender.Unknown,
                smokerStatus: client.smokerStatus || SmokerStatus.Unknown
            };
        });

        if (initialClients.length === 0) {
            initialClients.push(defaultAdult);
        }

        return initialClients;
    };

    return {
        build,
        buildClient,
        buildQuoteSettings,
        defaultAdult,
        defaultChild,
        defaultQuoteClient
    };
};

export default useSettingsBuilder;
